'use client';

import { Alert, AlertDescription, AlertTitle } from '#/components/ui/alert';
import { Button } from '#/components/ui/button';
import { DialogClose, DialogFooter } from '#/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '#/components/ui/form';
import { Input } from '#/components/ui/input';
import { HelpTooltip } from '#/components/v2/help-tooltip';
import { useTranslation } from '#/contexts/useTranslation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActor } from '@xstate/react';
import { ethers } from 'ethers';
import { Translation } from 'intl-lib';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useContext, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { StakingAddressInput } from './components/staking-address-input';
import { StakingTotalAmount } from './components/staking-total-amount';
import { MachineContext } from './contexts/machine';
import { InformationConstraintDialog } from './information-constraint';

export function StakingDialogInformation() {
  const translationApplication = useTranslation(
    translation => translation.Application
  );
  const translation = useTranslation(
    translation => translation.EthereumStaking.StakingAction
  );

  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const { withdrawaddr, claimaddr, count, amount } = state.context;

  const formSchema = useMemo(
    () =>
      z.object({
        withdrawaddr: z
          .string({
            required_error: 'Withdrawal address is required',
          })
          .refine(val => ethers.utils.isAddress(val), {
            message: translation.Information.WithdrawalAddress.ErrorMessage,
          }),
        claimaddr: z
          .string({
            required_error: 'Claim address is required',
          })
          .refine(val => ethers.utils.isAddress(val), {
            message: translation.Information.ClaimAddress.ErrorMessage,
          }),
        count: z
          .number({
            required_error: 'Please enter the number of validators',
            invalid_type_error: 'Please enter the number of validators',
          })
          .int()
          .min(1, translation.Information.Validators.MinErrorMessage)
          .max(100, translation.Information.Validators.MaxErrorMessage),
        amount: z.number().int().min(32).max(2048),
      }),
    [translation]
  );
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      count: count ?? 1,
      amount: amount ?? 32,
      withdrawaddr: withdrawaddr ?? '',
      claimaddr: claimaddr ?? '',
    },
  });

  const isManualSetRef = useRef(new Set<string>());
  const [isManualChangedDialogOpen, setIsManualChangedDialogOpen] =
    useState(false);

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (isManualSetRef.current.size > 0) {
      // show confirm dialog
      setIsManualChangedDialogOpen(true);
      return;
    }
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('onSubmit', values);
    const { withdrawaddr, claimaddr, count, amount } = values;
    dispatch({
      type: 'NEXT',
      data: {
        claimaddr,
        withdrawaddr,
        count,
        amount,
      },
    });
  }

  function onConfirm() {
    isManualSetRef.current.clear();

    form.handleSubmit(onSubmit)();
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col gap-3">
            <div className="font-bold">
              <Translation path="my_staking.staking_dialog.steps.0.validators_settings" />
            </div>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {translation.Information.Validators.Label}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={event =>
                            field.onChange(event.target.valueAsNumber)
                          }
                          type="number"
                          min="1"
                          max="100"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {translation.Information.Amount.Label}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={event =>
                            field.onChange(event.target.valueAsNumber)
                          }
                          type="number"
                          min="32"
                          max="2048"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="withdrawaddr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {translation.Information.WithdrawalAddress.Label}
                      <HelpTooltip
                        content={
                          translation.Information.WithdrawalAddress.LabelHelp
                        }
                      />
                    </FormLabel>
                    <FormControl>
                      <StakingAddressInput
                        useConnectedWallet
                        isManualSetRef={isManualSetRef}
                        placeholder={
                          translation.Information.WithdrawalAddress.Placeholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="claimaddr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {translation.Information.ClaimAddress.Label}
                      <HelpTooltip
                        content={translation.Information.ClaimAddress.LabelHelp}
                      />
                    </FormLabel>
                    <FormControl>
                      <StakingAddressInput
                        useConnectedWallet
                        isManualSetRef={isManualSetRef}
                        placeholder={
                          translation.Information.ClaimAddress.Placeholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="font-bold">
              <Translation path="my_staking.staking_dialog.steps.0.staking_summary" />
            </div>
            <div className="flex flex-col gap-4">
              <dl className="flex gap-2 items-center justify-between">
                <dt className="text-sm">
                  {translation.Information.Total.Label}
                </dt>
                <dd className="text-sm">
                  <StakingTotalAmount />
                </dd>
              </dl>
              <dl className="flex gap-2 items-center justify-between">
                <dt className="text-sm">
                  <Translation path="my_staking.order.validator_column.credential_type" />
                </dt>
                <dd className="text-sm">
                  {translation.FullCompounding.Label}{' '}
                  <HelpTooltip
                    content={translation.FullCompounding.LabelHelp}
                  />
                </dd>
              </dl>
            </div>
          </div>
          <Alert className="bg-yellow-50">
            <AlertTriangle className="w-4 h-5" />
            <AlertTitle>{translation.Information.Warning.Title}</AlertTitle>
            <AlertDescription>
              {translation.Information.Warning.Description}
            </AlertDescription>
          </Alert>
          <DialogFooter className="gap-2 sm:space-x-0">
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                {translationApplication.Dialog.CancelButtonLabel}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {translation.Information.ContinueButtonLabel}
            </Button>
          </DialogFooter>
        </form>
      </Form>
      <InformationConstraintDialog
        open={isManualChangedDialogOpen}
        onOpenChange={setIsManualChangedDialogOpen}
        onConfirm={onConfirm}
      />
    </>
  );
}
